// Partnership Models — default configuration + merge helper. Everything editable in Admin.

export const PM_DEFAULT = {
  scenarios: {
    conservative: { label: "Conservative", multiplier: 80 },
    projected: { label: "Projected", multiplier: 100 },
    high: { label: "High Performance", multiplier: 120 },
  },
  // paid_by per model: "hr" (Happy Retreats) or "owner"
  expenses: [
    { id: "fnb", name: "Food and beverage raw material", desc: "Ingredient and raw-material cost for meals and events.", type: "variable", pct: 18, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "payroll", name: "Permanent property payroll", desc: "Salaries of the permanent on-property team.", type: "variable", pct: 15, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "temp_manpower", name: "Event-day and temporary manpower", desc: "Additional staff engaged for event days.", type: "variable", pct: 4, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "housekeeping", name: "Housekeeping, laundry and guest supplies", desc: "Cleaning, linen, toiletries and guest consumables.", type: "variable", pct: 4, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "utilities", name: "Electricity, water, gas and fuel", desc: "Utility consumption across the property.", type: "variable", pct: 5, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "security", name: "Security", desc: "Security personnel and monitoring.", type: "variable", pct: 2, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "maintenance", name: "Routine repairs and maintenance", desc: "Day-to-day upkeep, minor repairs and servicing.", type: "variable", pct: 3, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "marketing", name: "Sales and marketing", desc: "Advertising, listings, photography and promotions.", type: "variable", pct: 6, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "commissions", name: "Agent commissions and payment charges", desc: "OTA/agent commissions and payment gateway fees.", type: "variable", pct: 4, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "admin_tech", name: "Administration, technology and licences", desc: "Software, permits, licences and admin overheads.", type: "variable", pct: 3, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "transport_misc", name: "Transport, waste disposal, pest control and miscellaneous", desc: "Logistics, waste handling, pest control and sundries.", type: "variable", pct: 2, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
    { id: "replacement", name: "Furniture and equipment replacement reserve", desc: "Reserve set aside for replacing furniture and equipment.", type: "variable", pct: 2, fixed_amount: 0, fixed_period: "annual", hybrid_fixed: 0, hybrid_pct: 0, paid_by: { fixed: "hr", management: "owner", mg: "hr" }, active: true },
  ],
  central_support_pct: 4,
  transparency: {
    heading: "Where every rupee goes",
    intro: "Full transparency — the complete revenue allocation under each model, with every expense assumption itemised. Nothing sits in the fine print.",
    show_hr_margin: false,
    expenses_label: "Property operating expenses",
    hr_label: "Happy Retreats — operations, brand & central support",
    fee_label: "Happy Retreats management fee",
    central_label: "Happy Retreats central support",
    margin_label: "Happy Retreats operating margin",
    owner_label: "Your income",
    breakdown_label: "See the full expense breakdown",
    breakdown_note: "These are industry-standard operating assumptions — every line is discussed, validated against your property and agreed together before any commercial terms are signed.",
  },
  stages: {
    enabled: true,
    question: "What stage is the property at?",
    note_template: "Partnership offers for properties at this stage typically fall in the {range} range. Final terms are agreed after a detailed property assessment.",
    readiness_note: "These percentages vary based on the readiness of the property and who funds the investment required to make it ready. Final terms are confirmed after a detailed property readiness assessment.",
    options: [
      { id: "new", label: "Brand new / unproven", desc: "Yet to open, or no operating history yet.", range_min: 8, range_max: 10, schedule: [{ upto_year: 2, pct: 10 }, { upto_year: 5, pct: 15 }, { upto_year: 0, pct: 20 }] },
      { id: "launched", label: "Recently launched (~1 year)", desc: "Operating for about a year with early revenue data.", range_min: 10, range_max: 12, schedule: [{ upto_year: 2, pct: 12 }, { upto_year: 5, pct: 15 }, { upto_year: 0, pct: 20 }] },
      { id: "established", label: "Established with proven revenues", desc: "Multi-year track record, limited capex needs, strong operating margins.", range_min: 12, range_max: 15, schedule: [{ upto_year: 2, pct: 18 }, { upto_year: 0, pct: 20 }] },
    ],
  },
  viability: { min_profit_pct: 15, healthy_pct: 15, review_pct: 10, opex_warn_pct: 75 },
  fixed_rental: {
    method: "percentage", // percentage | manual | sustainable
    rent_pct: 10,
    manual_monthly: 0,
    manual_annual: 0,
    override_annual: 0,
    escalation_pct: 0,
    rent_free_months: 0,
    lock_in_years: 0,
  },
  management: {
    fee_pct: 10,
    min_fee_enabled: false,
    min_monthly: 0,
    min_annual: 0,
    slabs_enabled: false,
    slabs: [],
    incentive_enabled: false,
    incentive_threshold: 0,
    incentive_pct: 0,
    override_fee_annual: 0,
  },
  mg_share: {
    payout_method: "higher_of", // higher_of | base_plus_upside | manual
    mg_pct: 10,
    mg_basis: "conservative", // conservative | projected
    manual_mg_monthly: 0,
    manual_mg_annual: 0,
    share_pct: 12,
    upside_threshold: 0,
    upside_pct: 0,
    manual_payout_annual: 0,
  },
  content: {
    heading: "Choose How You Would Like to Partner",
    intro: "Every property owner has a different expectation from their asset. Compare guaranteed income, higher growth potential and a balanced partnership structure before deciding what works for you.",
    preference_question: "What matters most to you?",
    preference_options: {
      guaranteed: "Guaranteed and predictable income",
      maximum: "Maximum earning potential",
      balanced: "A balance between safety and upside",
    },
    suggested_label: "Suggested based on your preference",
    upside_phrase: "Potential additional income available under a performance-linked model",
    protection_phrase: "Income protected if actual revenue performs below projections",
    disclaimer: "The figures shown are indicative projections based on the property information, revenue potential and operating assumptions currently available. Actual performance may vary depending on demand, seasonality, pricing, occupancy, event volume, operating costs, market conditions and property readiness. Final commercial terms will be confirmed only after detailed property assessment and mutual agreement.",
    cta_heading: "Let's Choose the Right Partnership Structure",
    cta_text: "Our team will review the property potential, operating requirements and your return expectations before recommending a final commercial structure.",
    cta_assessment: "Request Detailed Property Assessment",
    cta_download: "Download Comparison Report",
    cta_speak: "Speak With Happy Retreats",
  },
  models: {
    fixed: {
      name: "Fixed Rental by Happy Retreats",
      badge: "Low Risk",
      headline: "Guaranteed Returns. Minimal Operating Risk.",
      desc: "Happy Retreats leases and operates the property for an agreed fixed rent. The property owner receives predictable rental income without carrying the responsibility of daily operations, staffing, sales, marketing or fluctuating business performance.",
      risk_label: "Low Risk",
      income_type: "Fixed and Guaranteed",
      expenses_paid_by: "Happy Retreats",
      key_benefit: "Predictable cash flow with zero operating involvement",
      trade_off: "Limited participation in revenue growth",
      gains_title: "What You Gain",
      gives_title: "What You Give Up",
      gains: ["Guaranteed income", "Minimal operational involvement", "No staffing responsibility", "No daily operating risk", "Protection from seasonal fluctuations", "Predictable cash flow"],
      gives: ["Limited participation in revenue growth", "No upside from better-than-projected performance", "Lower earning potential when the property performs strongly"],
      owner_responsibility: ["Structural repairs", "Major capital replacement", "Property taxes", "Ownership-related compliance", "Major refurbishment", "Pre-existing property defects"],
      hr_responsibility: ["Day-to-day operations", "Property staffing", "Sales and marketing", "Utilities", "Routine maintenance", "Housekeeping", "Guest servicing", "Operating supplies", "Routine replenishment", "Event operations"],
    },
    management: {
      name: "Management Model",
      badge: "Higher Upside",
      headline: "Maximum Ownership. Maximum Upside.",
      desc: "The property owner retains the full business revenue and funds all property operating expenses. Happy Retreats operates, promotes and manages the property in return for an agreed percentage of the topline revenue.",
      risk_label: "Higher Risk",
      income_type: "Performance Linked",
      expenses_paid_by: "Property Owner",
      key_benefit: "Highest earning potential — you keep the business profit",
      trade_off: "You carry operating expenses and working capital",
      gains_title: "What You Gain",
      gives_title: "What You Carry",
      gains: ["Highest upside potential", "Full participation in revenue growth", "Greater financial visibility", "Property remains under the owner's commercial control", "Professional Happy Retreats management"],
      gives: ["Full operating expenses", "Working-capital requirement", "Revenue fluctuation risk", "Salary and vendor commitments", "Potential operating losses", "Capital and structural expenditure"],
      owner_responsibility: ["Property payroll", "Food cost", "Temporary manpower", "Utilities", "Security", "Housekeeping supplies", "Laundry", "Routine maintenance", "Sales and marketing expenses", "Commissions", "Technology", "Licences", "Operating supplies", "Working capital", "Replacement reserve", "Major capital expenses", "Structural expenses"],
      hr_responsibility: ["Brand positioning", "Sales", "Marketing management", "Revenue strategy", "Property operations", "Team management", "Guest experience", "SOPs", "Reviews and reputation management", "Reporting", "Training", "Quality control"],
      explain: "The management model can provide the highest earning potential because the owner retains the business profit after operating expenses and Happy Retreats' management fee. However, the owner is also responsible for operating losses, working capital, staff salaries, vendor payments, utility expenses, cost increases, seasonal fluctuations and major property expenditure.",
    },
    mg: {
      name: "Minimum Guarantee with Revenue Share",
      badge: "Balanced Option",
      headline: "Protected Income With Growth Participation.",
      desc: "The property owner receives a protected minimum return while retaining the opportunity to earn more when the property performs strongly.",
      risk_label: "Moderate Risk",
      income_type: "Protected Minimum With Performance Upside",
      expenses_paid_by: "Happy Retreats",
      key_benefit: "Protected minimum income with upside participation",
      trade_off: "Income above the minimum depends on performance",
      gains_title: "What You Gain",
      gives_title: "What You Share",
      gains: ["Protected minimum income", "Participation in property growth", "No responsibility for daily operations", "Lower risk than the management model", "Better upside than fixed rent", "Happy Retreats carries operating responsibility"],
      gives: ["Income fluctuates above the guaranteed minimum", "Earnings depend partly on property performance", "Owner return may be lower than the management model in a very strong year"],
      owner_responsibility: ["Structural repairs", "Major capital expenditure", "Major asset replacement", "Ownership-related taxes and compliance", "Pre-existing defects", "Major refurbishment"],
      hr_responsibility: ["Staffing", "Daily operations", "Sales and marketing", "Utilities", "Guest experience", "Routine maintenance", "Working capital", "Operating supplies", "Event delivery", "Routine replenishment"],
    },
  },
  comparison_rows: [
    { label: "Income nature", fixed: "Fixed", management: "Performance linked", mg: "Protected plus performance linked" },
    { label: "Owner risk", fixed: "Low", management: "High", mg: "Moderate" },
    { label: "Operating expenses paid by", fixed: "Happy Retreats", management: "Owner", mg: "Happy Retreats" },
    { label: "Working capital provided by", fixed: "Happy Retreats", management: "Owner", mg: "Happy Retreats" },
    { label: "Daily operations managed by", fixed: "Happy Retreats", management: "Happy Retreats", mg: "Happy Retreats" },
    { label: "Structural expenses paid by", fixed: "Owner", management: "Owner", mg: "Owner" },
    { label: "Income guarantee", fixed: "Yes", management: "No", mg: "Yes, minimum guarantee" },
    { label: "Revenue upside", fixed: "No", management: "Highest", mg: "Moderate to high" },
    { label: "Owner involvement", fixed: "Low", management: "Medium to high", mg: "Low" },
    { label: "Best suited for", fixed: "Predictability", management: "Maximum upside", mg: "Balance of safety and growth" },
  ],
  responsibilities: [
    { label: "Property operations", fixed: "Happy Retreats", management: "Happy Retreats manages", mg: "Happy Retreats" },
    { label: "Operating-expense funding", fixed: "Happy Retreats", management: "Owner", mg: "Happy Retreats" },
    { label: "Working capital", fixed: "Happy Retreats", management: "Owner", mg: "Happy Retreats" },
    { label: "Staffing", fixed: "Happy Retreats", management: "Owner funds, Happy Retreats manages", mg: "Happy Retreats" },
    { label: "Sales and marketing", fixed: "Happy Retreats", management: "Owner funds, Happy Retreats manages", mg: "Happy Retreats" },
    { label: "Routine maintenance", fixed: "Happy Retreats", management: "Owner funds", mg: "Happy Retreats" },
    { label: "Structural repairs", fixed: "Owner", management: "Owner", mg: "Owner" },
    { label: "Major capital expenditure", fixed: "Owner", management: "Owner", mg: "Owner" },
    { label: "Initial furniture and equipment", fixed: "Owner by default", management: "Owner", mg: "Owner by default" },
    { label: "Major asset replacement", fixed: "Owner", management: "Owner", mg: "Owner" },
    { label: "Operator negligence", fixed: "Happy Retreats", management: "Happy Retreats", mg: "Happy Retreats" },
  ],
};

const deepMergeObj = (def, stored) => {
  const out = { ...def };
  for (const k of Object.keys(stored || {})) {
    const sv = stored[k];
    if (sv && typeof sv === "object" && !Array.isArray(sv) && def[k] && typeof def[k] === "object" && !Array.isArray(def[k])) {
      out[k] = deepMergeObj(def[k], sv);
    } else if (sv !== undefined) {
      out[k] = sv;
    }
  }
  return out;
};

export const mergePM = (c) => {
  if (!c) return JSON.parse(JSON.stringify(PM_DEFAULT));
  const merged = deepMergeObj(PM_DEFAULT, c);
  if (!Array.isArray(c.expenses)) merged.expenses = JSON.parse(JSON.stringify(PM_DEFAULT.expenses));
  if (!Array.isArray(c.comparison_rows)) merged.comparison_rows = PM_DEFAULT.comparison_rows;
  if (!Array.isArray(c.responsibilities)) merged.responsibilities = PM_DEFAULT.responsibilities;
  if (!Array.isArray(c.stages?.options)) {
    merged.stages.options = JSON.parse(JSON.stringify(PM_DEFAULT.stages.options));
  } else {
    merged.stages.options = merged.stages.options.map((o) => {
      if (!Array.isArray(o.schedule)) {
        const def = PM_DEFAULT.stages.options.find((d) => d.id === o.id);
        return { ...o, schedule: def ? JSON.parse(JSON.stringify(def.schedule)) : [] };
      }
      return o;
    });
  }
  return merged;
};
